import type { ChangeEvent, FC, InputHTMLAttributes, ReactNode, Ref } from "react";
import { twMerge } from "tailwind-merge";

import { CheckIcon } from "mds/icons";
import { BUTTON_EVENT_CATEGORY, ComponentEventProperties, trackEvent } from "utils/amplitude";

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> &
  ComponentEventProperties & {
    children?: ReactNode;
    ref?: Ref<HTMLInputElement>;
  };

const INDICATOR_CLASSES = twMerge(
  "pointer-events-none flex size-4 items-center justify-center rounded-sm border border-black-tint-70 bg-transparent",
  "peer-hover:border-transparent peer-hover:bg-blue",
  "peer-checked:border-transparent peer-checked:bg-blue peer-checked:[&_svg]:opacity-100",
  "peer-disabled:cursor-not-allowed peer-disabled:border-black-tint-55 peer-disabled:bg-transparent",
  "peer-disabled:peer-hover:border-black-tint-55 peer-disabled:peer-hover:bg-transparent",
  "peer-checked:peer-disabled:bg-black-tint-55 peer-checked:peer-disabled:peer-hover:bg-black-tint-55",
  "peer-focus-visible:ring-2 peer-focus-visible:ring-blue peer-focus-visible:ring-offset-1",
);

export const Checkbox: FC<CheckboxProps> = ({
  children,
  className,
  ref,
  eventName,
  eventProperties,
  onChange,
  "aria-label": ariaLabel,
  ...inputProps
}) => {
  if (process.env.NODE_ENV !== "production" && !children && !ariaLabel) {
    console.error("Checkbox requires either children or an aria-label");
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (eventName) {
      trackEvent(eventName, {
        ...BUTTON_EVENT_CATEGORY,
        isEnabled: e.target.checked,
        ...eventProperties,
      });
    }
    onChange?.(e);
  };

  return (
    <label
      className={twMerge(
        "body-s flex items-center gap-2 text-black-tint-20 has-[:disabled]:cursor-not-allowed has-[:disabled]:text-black-tint-55",
        className,
      )}
    >
      <span className="relative size-4 shrink-0">
        <input
          {...inputProps}
          aria-label={ariaLabel}
          className="peer absolute inset-0 z-10 m-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          ref={ref}
          type="checkbox"
          onChange={handleChange}
        />
        <span className={INDICATOR_CLASSES} aria-hidden>
          <CheckIcon className="text-white opacity-0" size="s" />
        </span>
      </span>
      {children}
    </label>
  );
};

Checkbox.displayName = "Checkbox";
